import { useEffect, useState } from 'react'
import type { Vessel } from '@app-types/index'
import { httpClientService, vesselService, vesselStoreService } from '@services'

const isVessel = (value: unknown): value is Vessel => {
  if (!value || typeof value !== 'object') {
    return false
  }

  return 'vesselID' in value && 'vesselName' in value
}

const normalizeVesselResults = (data: unknown): Vessel[] => {
  if (Array.isArray(data)) {
    return data.filter(isVessel)
  }

  if (isVessel(data)) {
    return [data]
  }

  if (!data || typeof data !== 'object') {
    return []
  }

  const record = data as Record<string, unknown>
  const collection = record.vessels || record.results || record.items

  if (Array.isArray(collection)) {
    return collection.filter(isVessel)
  }

  return isVessel(record.vessel) ? [record.vessel] : []
}

const searchVessels = async (query: string, downloadPdf: boolean, idType?: string): Promise<Vessel[]> => {
  const statusResponse = await httpClientService.getCertificateStatus(query, downloadPdf, idType)
  return normalizeVesselResults(statusResponse.data)
}

const getVesselFromApi = async (vesselID: string | number): Promise<Vessel | null> => {
  const vessels = await searchVessels(String(vesselID), false, 'vesselId')
  return vessels.find((vessel) => String(vessel.vesselID) === String(vesselID)) || null
}

export const useVessels = () => {
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const fetchVessels = async () => {
      try {
        setLoading(true)
        const data = await vesselService.getAll()
        setVessels(data)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'))
      } finally {
        setLoading(false)
      }
    }

    void fetchVessels()
  }, [])

  const removeVessel = async (vesselID: string | number) => {
    try {
      await vesselService.remove(vesselID)
      setVessels((currentVessels) =>
        currentVessels.filter((vessel) => String(vessel.vesselID) !== String(vesselID))
      )
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'))
      throw err
    }
  }

  return { vessels, removeVessel, loading, error }
}

export const useVessel = (vesselID: string | number | undefined) => {
  const [vessel, setVessel] = useState<Vessel | null>(null)
  const [availableOffline, setAvailableOffline] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!vesselID) {
      setLoading(false)
      return
    }

    const fetchVessel = async () => {
      try {
        setLoading(true)
        const data = await vesselService.get(vesselID)
        const storedVessel = vesselStoreService.getVessel()
        const matchingStoredVessel =
          storedVessel && String(storedVessel.vesselID) === String(vesselID) ? storedVessel : null
        const apiVessel = data || matchingStoredVessel ? null : await getVesselFromApi(vesselID)

        if (apiVessel) {
          vesselStoreService.setVessel(apiVessel)
        }

        setVessel(data || matchingStoredVessel || apiVessel)
        setAvailableOffline(data != null)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'))
      } finally {
        setLoading(false)
      }
    }

    void fetchVessel()
  }, [vesselID])

  return { vessel, availableOffline, setAvailableOffline, loading, error }
}

export const useVesselSearch = (query: string, downloadPdf = true) => {
  const [results, setResults] = useState<Vessel[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const trimmedQuery = query.trim()

    if (!trimmedQuery) {
      setResults([])
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false

    const runSearch = async () => {
      try {
        setLoading(true)
        setError(null)

        const vessels = await searchVessels(trimmedQuery, downloadPdf)

        if (!cancelled) {
          setResults(vessels)
        }
      } catch (err) {
        if (!cancelled) {
          setResults([])
          setError(err instanceof Error ? err : new Error('Unknown error'))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void runSearch()

    return () => {
      cancelled = true
    }
  }, [query, downloadPdf])

  return { results, loading, error }
}
